import React, { useEffect, useRef, useState } from 'react';
import html2pdf from 'html2pdf.js';
import logo from '../Images/logo-main-black.png';
import './receipt.css';
import { formatMoney } from '../utils/currency';
import { extractPublicDocumentLookup, fetchPublicDocument, formatApiDate } from '../utils/publicDocsApi';

function ViewInvoice() {
  const [invoiceData, setInvoiceData] = useState(null);
  const [studentName, setStudentName] = useState('');
  const [admissionNumber, setAdmissionNumber] = useState('');
  const [currencyInfo, setCurrencyInfo] = useState({ formattedAmount: '', displayCurrency: 'KES' });
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [qrUnavailable, setQrUnavailable] = useState(false);
  const invoiceRef = useRef();

  const lookup = extractPublicDocumentLookup(window.location.search);
  const invoiceUrl = window.location.href;
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=0&data=${encodeURIComponent(invoiceUrl)}`;

  useEffect(() => {
    const fetchInvoice = async () => {
      setIsLoading(true);
      setLoadError('');
      setQrUnavailable(false);

      if (!lookup) {
        setLoadError('The invoice link is invalid.');
        setIsLoading(false);
        return;
      }

      try {
        const payload = await fetchPublicDocument('invoices', lookup);
        const nextInvoice = payload?.invoice || null;
        const nextStudent = payload?.student || {};
        const nextCurrency = payload?.currency || {};

        setInvoiceData(nextInvoice);
        setStudentName(nextStudent.displayName || nextStudent.bio || nextInvoice?.studentName || '');
        setAdmissionNumber(nextStudent.admissionNumber || nextInvoice?.prospectAdmissionNumber || '');
        setCurrencyInfo({
          formattedAmount: nextCurrency.formattedAmount || formatMoney(nextCurrency.displayAmount, nextCurrency.displayCurrency || nextInvoice?.currency || 'KES'),
          displayCurrency: nextCurrency.displayCurrency || nextInvoice?.currency || 'KES',
        });
      } catch (error) {
        console.error('Error fetching invoice:', error);
        setLoadError(error.message || 'We could not load this invoice right now.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvoice();
  }, [lookup]);

  const exportPDF = () => {
    if (!invoiceRef.current) return;

    html2pdf()
      .from(invoiceRef.current)
      .set({
        margin: 10,
        filename: `${invoiceData?.invoiceNumber || lookup || 'invoice'}_invoice.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      })
      .save();
  };

  if (isLoading) {
    return (
      <div className="receiptPage">
        <div className="receiptStateCard">
          <p className="receiptStateEyebrow">Fee invoice</p>
          <h2>Loading invoice...</h2>
          <p className="mb-0">Please wait while we prepare the invoice details.</p>
        </div>
      </div>
    );
  }

  if (loadError || !invoiceData) {
    return (
      <div className="receiptPage">
        <div className="receiptStateCard">
          <p className="receiptStateEyebrow">Fee invoice</p>
          <h2>Invoice unavailable</h2>
          <p className="mb-0">{loadError || 'We could not find the requested invoice.'}</p>
        </div>
      </div>
    );
  }

  const formattedInvoiceAmount = currencyInfo.formattedAmount || formatMoney(invoiceData.displayAmount || invoiceData.amountInKes || invoiceData.amount, currencyInfo.displayCurrency);
  const courseLabel = invoiceData.course || invoiceData.courseName || studentName || 'Fee invoice';
  const paymentRoute = invoiceData.paymentInstructionType === 'student_account_only'
    ? 'Pay from your student account'
    : 'Pay from your student account or college paybill';

  return (
    <div className="receiptPage">
      <div className="receiptShell">
        <div ref={invoiceRef} className="receiptCard">
          <div className="receiptHeader">
            <div className="receiptBrand">
              <img src={logo} className="receiptBrandLogo" alt="Ubunifu College" />
              <div>
                <p className="receiptEyebrow">Ubunifu College</p>
                <h1>Fee Invoice</h1>
                <p className="receiptMuted mb-0">Official student fee invoice</p>
              </div>
            </div>
            <div className="receiptHeaderMeta receiptDocumentMeta">
              <div className="receiptMetaStack">
                <span className="receiptMetaLabel">Invoice No.</span>
                <strong>{invoiceData.invoiceNumber}</strong>
              </div>
              <div className="receiptMetaStack">
                <span className="receiptMetaLabel">Issue date</span>
                <strong>{formatApiDate(invoiceData.timeCreated)}</strong>
              </div>
            </div>
          </div>

          <div className="receiptDivider" />

          <div className="receiptTopGrid">
            <div className="receiptPartyBlock">
              <p className="receiptSectionEyebrow">Billed to</p>
              <h2>{studentName || 'Student record unavailable'}</h2>
              <p className="receiptPartyLine">Admission No. {admissionNumber || '--'}</p>
              <div className="receiptMetaList">
                <div className="receiptMetaListRow">
                  <span>Programme</span>
                  <strong>{courseLabel}</strong>
                </div>
                <div className="receiptMetaListRow">
                  <span>Due date</span>
                  <strong>{formatApiDate(invoiceData.dueDate)}</strong>
                </div>
                <div className="receiptMetaListRow">
                  <span>Payment route</span>
                  <strong>{paymentRoute}</strong>
                </div>
              </div>
            </div>

            <div className="receiptQrPanel">
              <p className="receiptSectionEyebrow">Invoice QR</p>
              <div className="receiptQrFrame">
                {qrUnavailable ? (
                  <div className="receiptQrFallback">
                    <span>Invoice link</span>
                    <strong>{invoiceData.invoiceNumber}</strong>
                  </div>
                ) : (
                  <img
                    src={qrCodeUrl}
                    alt={`QR code for invoice ${invoiceData.invoiceNumber}`}
                    className="receiptQrImage"
                    crossOrigin="anonymous"
                    onError={() => setQrUnavailable(true)}
                  />
                )}
              </div>
              <p className="receiptQrCaption mb-0">Scan to open this invoice online.</p>
            </div>
          </div>

          <div className="receiptSnapshotGrid">
            <div className="receiptSnapshotItem">
              <span className="receiptMetaLabel">Amount due</span>
              <strong>{formattedInvoiceAmount}</strong>
            </div>
            <div className="receiptSnapshotItem">
              <span className="receiptMetaLabel">Due date</span>
              <strong>{formatApiDate(invoiceData.dueDate)}</strong>
            </div>
            <div className="receiptSnapshotItem">
              <span className="receiptMetaLabel">Reference</span>
              <strong>{invoiceData.invoiceNumber}</strong>
            </div>
          </div>

          <div className="receiptSupportGrid">
            <div className="receiptSupportCard">
              <p className="receiptSectionEyebrow">Payment instructions</p>
              <div className="receiptMetaList receiptMetaList--compact">
                <div className="receiptMetaListRow">
                  <span>Student account</span>
                  <strong>Open your fee invoices page to pay this invoice.</strong>
                </div>
                {invoiceData.paybill ? (
                  <div className="receiptMetaListRow">
                    <span>Paybill</span>
                    <strong>{invoiceData.paybill}</strong>
                  </div>
                ) : null}
                {invoiceData.accountNumber ? (
                  <div className="receiptMetaListRow">
                    <span>Account number</span>
                    <strong>{invoiceData.accountNumber}</strong>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="receiptSupportCard">
              <p className="receiptSectionEyebrow">Notes</p>
              <p className="receiptSupportCopy">
                Keep this invoice reference for payment follow-up and future fee reconciliation. Once payment is received, your receipt will be available in your student account.
              </p>
            </div>
          </div>

          <div className="receiptTableWrap">
            <table className="table receiptTable mb-0">
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Due Date</th>
                  <th className="text-end">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td data-label="Description">{courseLabel}</td>
                  <td data-label="Due Date">{formatApiDate(invoiceData.dueDate)}</td>
                  <td data-label="Amount" className="text-end">{formattedInvoiceAmount}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="receiptTotalBar">
            <span>Total due</span>
            <strong>{formattedInvoiceAmount}</strong>
          </div>

          <div className="receiptFooter">
            <div className="receiptFooterMeta">
              <span>This invoice is computer-generated and does not require a signature.</span>
              <strong>Reference: {invoiceData.invoiceNumber}</strong>
            </div>
            <p className="mb-0">Use the student portal to complete payment before the due date shown above.</p>
          </div>
        </div>

        <div className="receiptActions">
          <button onClick={exportPDF} className="receiptDownloadBtn">
            Download as PDF
          </button>
        </div>
      </div>
    </div>
  );
}

export default ViewInvoice;
